export const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      projects: 'Projects',
      about: 'About',
      cv: 'CV',
    },
    // Hero
    hero: {
      downloadCV: 'Download CV',
      contactMe: 'Contact Me',
      email: 'Email',
      github: 'GitHub',
      languages: 'Languages',
    },
    // Projects page
    projects: {
      title: 'All Projects',
      description:
        'Explore my portfolio of professional work, personal projects, and demo implementations.',
      search: 'Search',
      searchPlaceholder: 'Title, tags, description...',
      type: 'Type',
      allTypes: 'All Types',
      sortBy: 'Sort By',
      sortTitle: 'Title',
      sortType: 'Type',
      showing: 'Showing {count} projects',
      clearFilters: 'Clear filters',
      noResults: 'No projects found matching your filters.',
      viewAll: 'View All →',
    },
    // Project details
    projectDetail: {
      public: 'Public',
      private: 'Private',
      viewGithub: 'View on GitHub →',
      githubShort: 'GitHub →',
      theStory: 'The Story',
      problem: 'Problem',
      action: 'Action',
      result: 'Result',
      keyOutcomes: 'Key Outcomes',
      technologies: 'Technologies Used',
      tags: 'Tags:',
      architecture: 'Architecture',
      opsLayer: 'Ops Layer',
      cicd: 'CI/CD',
      infrastructure: 'Infrastructure',
      observability: 'Observability',
      outcomes: 'Outcomes',
      codeSnippets: 'Code Snippets',
      roleScope: 'Role/Scope:',
      stack: 'Stack:',
    },
    // Skills
    skills: {
      title: 'Skills',
      softSkills: 'Soft Skills',
      skill: 'Skill',
      level: 'Level',
      source: 'Source',
      evidence: 'Evidence',
    },
    // About page
    about: {
      title: 'About Me',
      languages: 'Languages',
      availability: 'Availability',
      targetRole: 'Target Role:',
      startDate: 'Start Date:',
      duration: 'Duration:',
      rhythm: 'Rhythm:',
      school: 'School:',
      openTo: 'Open to:',
      education: 'Education',
      experience: 'Professional Experience',
      highlights: 'Highlights:',
      technologies: 'Technologies:',
      connect: "Let's Connect",
    },
    // Home sections
    home: {
      featuredProjects: 'Featured Projects',
      aiTitle: 'AI-Enhanced DevOps Engineer',
      aiDescription:
        'AI-enhanced workflows with a security-first approach: no client code or secrets sent to public models. I leverage Claude Code and open-source tools for architecture and automation while keeping sensitive work offline.',
      privacyTitle: '🔒 Privacy-First Development',
      privacyDesc:
        'Claude Code & OpenCode with VSCodium for local control. Public APIs for architecture & docs. Client code and proprietary logic stay offline.',
      devopsTitle: '🏗️ DevOps Automation',
      devopsDesc:
        'AI-assisted IaC (Terraform), CI/CD optimization, observability patterns—where AI truly adds value.',
      selectiveTitle: '🎯 Selective Enhancement',
      selectiveDesc:
        'Architecture & refactoring: AI-assisted. Business logic & production secrets: human-controlled. Context matters.',
    },
    // Highlights
    highlights: {
      certifications: 'Certifications',
      availability: 'Availability',
    },
    // Contact
    contact: {
      title: 'Get in Touch',
      description: "Interested in working together? Let's connect!",
      sendEmail: 'Send Email',
      linkedin: 'LinkedIn',
      github: 'GitHub',
    },
    // Assistant
    assistant: {
      title: 'Portfolio Assistant',
      subtitle: 'Let me guide you',
      close: 'Close assistant',
      chooseIntent: "Choose what you'd like to explore:",
      backToMenu: '← Back to menu',
      footer: 'This assistant uses predefined intents to guide your navigation.',
      actionExecuted: 'Action executed! Navigate to see the result.',
      // Intents
      intents: {
        realtimeMarketPipeline: {
          label: 'Real-time Market Pipeline',
          description: 'Production streaming platform with live monitoring dashboard',
          response:
            'Switching to engineer view to show the Real-time Market Pipeline — a live 24/7 production streaming platform on GCP with multi-source ingestion (Binance WebSocket + CoinGecko API), Kafka, dual-write to BigQuery + GCS, dbt transformations, and public Grafana Cloud monitoring dashboard.',
        },
        recruiterOverview: {
          label: 'Recruiter Overview',
          description: 'Get a quick overview focused on outcomes and achievements',
          response:
            'Switching to recruiter view to highlight key outcomes, certifications, and professional achievements. Perfect for understanding impact at a glance.',
        },
        engineerOverview: {
          label: 'Engineer Overview',
          description: 'Technical deep dive with architecture and implementation details',
          response:
            'Switching to engineer view to show technical depth, architecture patterns, and DevOps practices. Includes code snippets, ops layers, and infrastructure details.',
        },
        gamerView: {
          label: 'Gamer View',
          description: 'Interactive CV as an idle game showcase',
          response:
            'Switching to gamer persona! Experience my professional journey as an idle/clicker game with all skills unlocked and resources generating. A unique way to explore my CV!',
        },
        engineerCicd: {
          label: 'CI/CD Deep Dive',
          description: 'Explore CI/CD pipeline demos and production-ready practices',
          response:
            'Switching to engineer view to show the CI/CD production pipeline demo with GitHub Actions, Docker security scanning, and deployment automation.',
        },
        engineerData: {
          label: 'Data Pipeline Demo',
          description: 'See CityPulse ETL with Airflow + DuckDB orchestration',
          response:
            "Here's the CityPulse ETL pipeline demo with Apache Airflow orchestration, DuckDB warehouse, automated data quality checks, and layered architecture (Raw → Staging → Analytics). Built for reliability and reproducibility.",
        },
        aiDataAssistant: {
          label: 'AI-Powered Data Assistant',
          description: 'Multi-agent system for data engineering workflows',
          response:
            'Switching to engineer view to show the AI-Powered Data Assistant with three specialized agents (SQL Generator, Quality Check Suggestor, Pipeline Debugger), LangGraph orchestration, and local LLM inference with Ollama.',
        },
        terraformMulticloud: {
          label: 'Terraform Multi-Cloud IaC',
          description: 'Reusable IaC modules for AWS and GCP deployment',
          response:
            'Switching to engineer view to show the Terraform Multi-Cloud IaC demo with reusable modules for AWS and GCP, security scanning (tfsec), and automated validation via GitHub Actions.',
        },
        aiTools: {
          label: 'AI-Assisted Engineering',
          description: 'How I use AI tools in my workflow',
          response:
            "I'm an AI-assisted engineer, not an AI engineer. I use Claude, GitHub Copilot, and custom automation to enhance productivity while maintaining deep technical understanding.",
        },
        contact: {
          label: 'Get in Touch',
          description: 'Contact information and availability',
          response:
            "I'm currently seeking a Data Engineering apprenticeship starting January 2026. Let's connect via email, LinkedIn, or GitHub!",
        },
      },
    },
    // Footer
    footer: {
      builtWith: 'Built with React + Vite + TailwindCSS.',
      email: 'Email',
    },
    // Common
    common: {
      loading: 'Loading...',
      outcomes: 'Outcomes:',
    },
    // Persona tooltips
    persona: {
      recruiter: 'Recruiter view - Outcome-focused, highlights achievements and certifications',
      engineer: 'Engineer view - Technical depth with architecture and implementation details',
    },
  },
  fr: {
    // Navigation
    nav: {
      home: 'Accueil',
      projects: 'Projets',
      about: 'À propos',
      cv: 'CV',
    },
    // Hero
    hero: {
      downloadCV: 'Télécharger CV',
      contactMe: 'Me contacter',
      email: 'Email',
      github: 'GitHub',
      languages: 'Langues',
    },
    // Projects page
    projects: {
      title: 'Tous les projets',
      description:
        'Explorez mon portfolio de projets professionnels, personnels et de démonstration.',
      search: 'Rechercher',
      searchPlaceholder: 'Titre, tags, description...',
      type: 'Type',
      allTypes: 'Tous les types',
      sortBy: 'Trier par',
      sortTitle: 'Titre',
      sortType: 'Type',
      showing: 'Affichage de {count} projets',
      clearFilters: 'Effacer les filtres',
      noResults: 'Aucun projet ne correspond à vos filtres.',
      viewAll: 'Voir tout →',
    },
    // Project details
    projectDetail: {
      public: 'Public',
      private: 'Privé',
      viewGithub: 'Voir sur GitHub →',
      githubShort: 'GitHub →',
      theStory: "L'histoire",
      problem: 'Problème',
      action: 'Action',
      result: 'Résultat',
      keyOutcomes: 'Résultats clés',
      technologies: 'Technologies utilisées',
      tags: 'Tags :',
      architecture: 'Architecture',
      opsLayer: 'Couche Ops',
      cicd: 'CI/CD',
      infrastructure: 'Infrastructure',
      observability: 'Observabilité',
      outcomes: 'Résultats',
      codeSnippets: 'Extraits de code',
      roleScope: 'Rôle/Périmètre :',
      stack: 'Stack :',
    },
    // Skills
    skills: {
      title: 'Compétences',
      softSkills: 'Compétences relationnelles',
      skill: 'Compétence',
      level: 'Niveau',
      source: 'Source',
      evidence: 'Preuves',
    },
    // About page
    about: {
      title: 'À propos',
      languages: 'Langues',
      availability: 'Disponibilité',
      targetRole: 'Poste visé :',
      startDate: 'Date de début :',
      duration: 'Durée :',
      rhythm: 'Rythme :',
      school: 'École :',
      openTo: 'Ouvert à :',
      education: 'Formation',
      experience: 'Expérience professionnelle',
      highlights: 'Points clés :',
      technologies: 'Technologies :',
      connect: 'Restons en contact',
    },
    // Home sections
    home: {
      featuredProjects: 'Projets mis en avant',
      aiTitle: "Ingénieur DevOps amélioré par l'IA",
      aiDescription:
        "Workflows améliorés par l'IA avec une approche sécurité d'abord : aucun code client ou secret envoyé aux modèles publics. J'utilise Claude Code et des outils open-source pour l'architecture et l'automatisation tout en gardant le travail sensible hors ligne.",
      privacyTitle: '🔒 Développement axé sur la confidentialité',
      privacyDesc:
        "Claude Code & OpenCode avec VSCodium pour un contrôle local. APIs publiques pour l'architecture & docs. Le code client et la logique propriétaire restent hors ligne.",
      devopsTitle: '🏗️ Automatisation DevOps',
      devopsDesc:
        "IaC assisté par IA (Terraform), optimisation CI/CD, patterns d'observabilité—là où l'IA apporte vraiment de la valeur.",
      selectiveTitle: '🎯 Amélioration sélective',
      selectiveDesc:
        "Architecture & refactoring : assisté par IA. Logique métier & secrets de production : contrôlés par l'humain. Le contexte compte.",
    },
    // Highlights
    highlights: {
      certifications: 'Certifications',
      availability: 'Disponibilité',
    },
    // Contact
    contact: {
      title: 'Contactez-moi',
      description: 'Intéressé par une collaboration ? Restons en contact !',
      sendEmail: 'Envoyer un email',
      linkedin: 'LinkedIn',
      github: 'GitHub',
    },
    // Assistant
    assistant: {
      title: 'Assistant Portfolio',
      subtitle: 'Laissez-moi vous guider',
      close: "Fermer l'assistant",
      chooseIntent: 'Choisissez ce que vous souhaitez explorer :',
      backToMenu: '← Retour au menu',
      footer: 'Cet assistant utilise des intentions prédéfinies pour guider votre navigation.',
      actionExecuted: 'Action exécutée ! Naviguez pour voir le résultat.',
      // Intents
      intents: {
        realtimeMarketPipeline: {
          label: 'Pipeline Market Temps Réel',
          description: 'Plateforme streaming production avec dashboard monitoring live',
          response:
            'Passage en vue ingénieur pour montrer le Pipeline Market Temps Réel — une plateforme streaming production live 24/7 sur GCP avec ingestion multi-source (Binance WebSocket + API CoinGecko), Kafka, dual-write vers BigQuery + GCS, transformations dbt, et dashboard public Grafana Cloud de monitoring.',
        },
        recruiterOverview: {
          label: 'Vue Recruteur',
          description: 'Aperçu rapide axé sur les résultats et réalisations',
          response:
            "Passage en vue recruteur pour mettre en avant les résultats clés, certifications et réalisations professionnelles. Parfait pour comprendre l'impact en un coup d'œil.",
        },
        engineerOverview: {
          label: 'Vue Ingénieur',
          description: "Plongée technique avec architecture et détails d'implémentation",
          response:
            "Passage en vue ingénieur pour montrer la profondeur technique, les patterns d'architecture et les pratiques DevOps. Inclut extraits de code, couches ops et détails d'infrastructure.",
        },
        gamerView: {
          label: 'Vue Gamer',
          description: 'CV interactif sous forme de jeu idle',
          response:
            "Passage en persona gamer ! Découvrez mon parcours professionnel sous forme de jeu idle/clicker avec toutes les compétences débloquées et les ressources en génération. Une façon unique d'explorer mon CV !",
        },
        engineerCicd: {
          label: 'CI/CD en Profondeur',
          description: 'Explorez les démos de pipelines CI/CD et pratiques production',
          response:
            'Passage en vue ingénieur pour montrer la démo de pipeline CI/CD production avec GitHub Actions, scan de sécurité Docker et automatisation du déploiement.',
        },
        engineerData: {
          label: 'Démo Pipeline Data',
          description: 'Voir CityPulse ETL avec orchestration Airflow + DuckDB',
          response:
            'Voici la démo du pipeline ETL CityPulse avec orchestration Apache Airflow, entrepôt DuckDB, contrôles qualité automatisés et architecture en couches (Raw → Staging → Analytics). Conçu pour la fiabilité et la reproductibilité.',
        },
        aiDataAssistant: {
          label: 'Assistant IA Data',
          description: 'Système multi-agent pour workflows data engineering',
          response:
            "Passage en vue ingénieur pour montrer l'Assistant IA Data avec trois agents spécialisés (Générateur SQL, Suggesteur de contrôles qualité, Debugger de pipelines), orchestration LangGraph et inférence LLM locale avec Ollama.",
        },
        terraformMulticloud: {
          label: 'Terraform Multi-Cloud IaC',
          description: 'Modules IaC réutilisables pour déploiement AWS et GCP',
          response:
            'Passage en vue ingénieur pour montrer la démo Terraform Multi-Cloud IaC avec modules réutilisables pour AWS et GCP, scan de sécurité (tfsec) et validation automatisée via GitHub Actions.',
        },
        aiTools: {
          label: 'Ingénierie assistée par IA',
          description: "Comment j'utilise les outils IA dans mon workflow",
          response:
            "Je suis un ingénieur assisté par IA, pas un ingénieur IA. J'utilise Claude, GitHub Copilot et de l'automatisation personnalisée pour améliorer la productivité tout en maintenant une compréhension technique profonde.",
        },
        contact: {
          label: 'Me contacter',
          description: 'Informations de contact et disponibilité',
          response:
            'Je recherche actuellement une alternance en Data Engineering à partir de janvier 2026. Contactez-moi par email, LinkedIn ou GitHub !',
        },
      },
    },
    // Footer
    footer: {
      builtWith: 'Développé avec React + Vite + TailwindCSS.',
      email: 'Email',
    },
    // Common
    common: {
      loading: 'Chargement...',
      outcomes: 'Résultats :',
    },
    // Persona tooltips
    persona: {
      recruiter:
        'Vue recruteur - Axée sur les résultats, met en avant les réalisations et certifications',
      engineer:
        "Vue ingénieur - Profondeur technique avec architecture et détails d'implémentation",
    },
  },
} as const

export type TranslationKey = string
